import { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Cómo se calcula **este** número, en cuatro respuestas y con los datos que vinieron con él.
 *
 * **Por qué existe.** Diego lo pidió el 2026-10-05: *«no está claro para nadie qué significa cada
 * indicador… hacen falta ayudas contextuales que avisen, en función de cómo funciona el sistema y
 * según las parametrizaciones aplicadas, cuál es el criterio que se toma para determinar cada
 * valor»*. Y el bloqueo era físico: `KpiCard` **no aceptaba tooltip**, sólo un `sub` de una línea,
 * así que 22 indicadores no tenían dónde explicarse.
 *
 * **Por qué no es un texto fijo.** Un porcentaje de cumplimiento significa cosas distintas según el
 * SLA que declare cada tipo de pedido, según el horario del área y según el modo en que esa cápsula
 * detiene su reloj de atención. Una ayuda escrita a mano queda vieja el día que alguien cambia la
 * configuración, y nadie se entera: es una segunda declaración del mismo hecho, que es el defecto
 * que este producto viene sacando desde el lunes.
 *
 * Así que la ayuda se **arma con el dato**: el universo lo manda el backend junto al número
 * (`medidas`, `sin_reloj`, `sin_declarar`, `medible_en`…) y los parámetros vigentes salen de la
 * configuración de la cápsula. **El modelo es el Gantt**, que es la pantalla que nadie cuestiona:
 * `explicarFila` reproduce la cuenta del backend con la jornada real —*«16 h ÷ 9 h por día = 2
 * días»*— y por eso su ayuda no puede mentir.
 *
 * **Las cuatro respuestas, en este orden**, que es el orden en que alguien las necesita:
 *
 * 1. `pregunta` — qué pregunta contesta este número, en una línea y sin nombres técnicos;
 * 2. `cuenta` — la cuenta en palabras;
 * 3. `universo` — sobre cuántos se midió, y **qué quedó afuera y por qué**;
 * 4. `parametro` — qué configuración de tu cápsula lo cambia, con el valor que tiene hoy.
 *
 * `ojo` va último y en ámbar, para lo que hace decidir mal si no se sabe.
 *
 * **Arranca cerrada**, igual que `QueSeConfiguraAca`: quien ya sabe no la esquiva cada vez.
 */
export default function ComoSeCalcula({ pregunta, cuenta, universo, parametro, ojo, testId }) {
  const [abierta, setAbierta] = useState(false);

  if (!pregunta && !cuenta && !universo && !parametro) return null;

  return (
    <div className="mt-1.5" data-testid={testId || 'como-se-calcula'}>
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="flex items-center gap-1 text-[10px] text-brand-primary hover:text-[#001F6D]"
        aria-expanded={abierta}
      >
        <HelpCircle className="w-3 h-3" />
        Cómo se calcula
        {abierta ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>

      {abierta && (
        <div className="mt-1 space-y-1 border-l-2 border-brand-border pl-2">
          {pregunta && (
            <p className="text-[10px] text-zinc-600 leading-snug">
              <span className="text-zinc-400">Contesta: </span>{pregunta}
            </p>
          )}
          {cuenta && (
            <p className="text-[10px] text-zinc-600 leading-snug">
              <span className="text-zinc-400">La cuenta: </span>{cuenta}
            </p>
          )}
          {/* El denominador y lo excluido van juntos: «60 %» sobre tres casos y sobre ochenta son
              el mismo número con significados distintos, y lo que quedó afuera explica por qué el
              denominador no es el total. */}
          {universo && (
            <p className="text-[10px] text-zinc-600 leading-snug">
              <span className="text-zinc-400">Sobre qué: </span>{universo}
            </p>
          )}
          {parametro && (
            <p className="text-[10px] text-zinc-600 leading-snug">
              <span className="text-zinc-400">Lo cambia: </span>{parametro}
            </p>
          )}
          {ojo && (
            <p className="text-[10px] text-[#8A6400] bg-[#FFFCF2] border border-amber-200 rounded-sm px-1.5 py-1 leading-snug">
              {ojo}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
